import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Loader2, Shield, Star, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function BetaTestersPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchTesters = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/beta-testers`);
      setData(res.data);
      setLastRefreshedAt(Date.now());
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTesters(); }, []);

  const handleRevoke = async (userId, name) => {
    setRevoking(userId);
    try {
      await axios.post(`${API}/admin/beta/revoke/${userId}`);
      toast.success(`Revoked beta from ${name}. Badge preserved.`);
      fetchTesters();
    } catch { toast.error("Failed to revoke"); }
    finally { setRevoking(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-purple-500" /></div>;
  if (!data || data.count === 0) return <p className="text-sm text-slate-400 text-center py-6" data-testid="beta-empty">No beta testers yet</p>;

  return (
    <div className="space-y-3" data-testid="beta-testers-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchTesters} lastRefreshedAt={lastRefreshedAt} testId="beta-testers-refresh" /></div>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-500">{data.count} Beta Tester{data.count !== 1 ? "s" : ""}</p>
      </div>

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {data.testers.map((t) => (
          <div
            key={t.user_id}
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100"
            data-testid={`beta-tester-${t.user_id}`}
          >
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              {t.is_founding ? (
                <Star className="w-4 h-4 text-amber-500" />
              ) : (
                <Shield className="w-4 h-4 text-purple-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-slate-700 truncate">{t.name || t.email}</p>
                {t.is_founding && (
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-100 text-amber-700 flex-shrink-0">FOUNDING</span>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                {t.points_earned} pts · {t.badge_count} badges · {t.multiplier}x
              </p>
            </div>
            <button
              onClick={() => handleRevoke(t.user_id, t.name)}
              disabled={revoking === t.user_id}
              className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 text-[10px] font-bold hover:bg-red-100 transition flex items-center gap-1 flex-shrink-0 disabled:opacity-50"
              data-testid={`revoke-beta-${t.user_id}`}
            >
              {revoking === t.user_id ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <XCircle className="w-3 h-3" />
              )}
              Revoke
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
